import { easings, animated, useSpringRef, useSpring } from '@react-spring/web';
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import LoadingSpinner from 'src/common/components/loading-spinner';
import FileDropzone from 'src/common/FileDropzone';
import { CheckmarkRegularIcon, CloseRegularIcon } from 'src/monet/icons';
import Papa from 'papaparse';
import { EMAIL_GLOBAL_REGEX } from './email-capture';

interface BulkEmailUploadModalProps {
  emails: string[];
  type: 'whitelist' | 'blacklist';
  onImportClick: (emails: string[]) => void;
}

export interface BulkEmailUploadModalRef {
  open: () => void;
  close: () => void;
}

const ANIMATION_CONFIG = {
  mass: 1,
  tension: 400,
  friction: 40,
};

const BULK_EMAIL_TEMPLATE_URL = 'https://assets-raw.jny.io/static/Email+upload+template.csv';

const parseCSV = (file: File): Promise<string[]> =>
  new Promise((resolve) => {
    return Papa.parse(file, {
      complete: ({ data }) => {
        if (data.length === 0) {
          resolve([]);
          return;
        }
        const emailList = data.filter((line) => EMAIL_GLOBAL_REGEX.test(line as string));
        resolve(emailList.flat() as string[]);
        return;
      },
    });
  });

enum Status {
  IDLE = 0,
  ONGOING = 1,
  SUCCESS = 2,
  FAILED = 3,
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const BulkEmailUploadModalComponent: ForwardRefRenderFunction<BulkEmailUploadModalRef, BulkEmailUploadModalProps> = (
  { type, emails, onImportClick },
  ref
) => {
  const [show, setShow] = useState(false);
  const fileUploadRef = useRef<HTMLElement | null>(null);
  const [fileStatusMapping, setFileStatusMapping] = useState<Record<string, Status>>({});
  const [uploadProgress, setUploadProgress] = useState<Status>(Status.IDLE);
  const [statusMessage, setStatusMessage] = useState('');
  const bulkEmailListRef = useRef<string[]>([]);
  const uniqueBulkEmailListRef = useRef<string[]>([]);

  const animationApi = useSpringRef();
  const [animationStyle] = useSpring(
    () => ({
      ref: animationApi,
      from: { opacity: 0, scale: 0.9 },
      to: { opacity: 1, scale: 1 },
      config: ANIMATION_CONFIG,
    }),
    []
  );

  const handleOpenModal = () => {
    setShow(true);
  };

  const handleCloseModal = () => {
    setShow(false);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpenModal,
    close: handleCloseModal,
  }));

  useEffect(() => {
    if (show) {
      animationApi.start({
        from: { opacity: 0, scale: 0.9 },
        to: { opacity: 1, scale: 1 },
        config: ANIMATION_CONFIG,
      });
    } else {
      animationApi.start({
        to: { opacity: 0, scale: 0.9 },
        config: { duration: 200, easing: easings.easeOutCubic },
      });
    }
  }, [show, animationApi]);

  const onFilesUpdate = useCallback(async (files: Array<File>) => {
    let fileCounter = 0;
    const statusMapping = files.reduce((acc, file) => ({ ...acc, [file.name]: Status.IDLE }), fileStatusMapping);
    setFileStatusMapping(statusMapping);
    setStatusMessage('Initializing');
    setUploadProgress(Status.ONGOING);
    for (const file of files) {
      setFileStatusMapping((mapping) => ({
        ...mapping,
        [file.name]: Status.ONGOING,
      }));
      const csvResult = await parseCSV(file);
      bulkEmailListRef.current = [...bulkEmailListRef.current, ...csvResult].filter((email) => !emails.includes(email));
      fileCounter++;
      await sleep(4_00);
      if (files.length - fileCounter > 1) {
        setStatusMessage(`${files.length - fileCounter} files remaining`);
      }
      setFileStatusMapping((mapping) => ({
        ...mapping,
        [file.name]: Status.SUCCESS,
      }));
    }
    setUploadProgress(Status.SUCCESS);
    const uniqueBulkEmailList = [...new Set(bulkEmailListRef.current)];
    uniqueBulkEmailListRef.current = uniqueBulkEmailList;
    if (uniqueBulkEmailList.length > 0) {
      setStatusMessage(`${uniqueBulkEmailList.length} email will be imported`);
    } else {
      setStatusMessage(`All emails are already ${type}ed`);
    }
  }, []);

  if (!show) {
    return null;
  }

  const handleOnClickUpload = () => {
    if (!fileUploadRef.current) {
      return;
    }
    fileUploadRef.current.click();
  };

  const isImportDisabled = uploadProgress !== Status.SUCCESS;
  const isUploadOngoing = uploadProgress === Status.ONGOING;

  return (
    <div className='fixed inset-0 z-[9999] !mt-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm'>
      <animated.div
        style={animationStyle}
        className='bg-white rounded-xl p-8 w-[560px] shadow-2xl text-gray-800 relative'
      >
        <button
          className='absolute right-6 top-6 text-gray-500 hover:text-gray-800 transition-colors'
          onClick={handleCloseModal}
        >
          <CloseRegularIcon className='w-6 h-6' />
        </button>
        <h2 className='text-2xl font-semibold mb-4 text-gray-800 capitalize'>{type} emails</h2>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col'>
            <p className='text-bedrock-p text-bedrock-black'>
              <a className='underline' href={BULK_EMAIL_TEMPLATE_URL} target='_blank' rel='noopener noreferrer'>
                Download the template
              </a>
              , fill and select it.
            </p>
            <p className='text-bedrock-p text-bedrock-black'>
              Once the process is completed, emails will be ready to be {type}ed.
            </p>
          </div>
          <div className='hidden'>
            <FileDropzone
              accept={'.csv'}
              type='file'
              className='file-dropzone'
              multiple
              onFilesUpdate={onFilesUpdate}
              resetOnSuccess={true}
              dropzoneClasses={null}
              inputRef={fileUploadRef}
            />
          </div>
          <div className='flex flex-col gap-2'>
            {Object.entries(fileStatusMapping).map(([fileName, status], index) => {
              return (
                <div key={`upload-csv-${index}`} className='flex w-full justify-between items-center'>
                  <span className='w-full text-bedrock-black text-bedrock-p'>{fileName}</span>
                  {status === Status.ONGOING ? (
                    <LoadingSpinner containerClassName='!w-auto' />
                  ) : status === Status.SUCCESS ? (
                    <CheckmarkRegularIcon />
                  ) : null}
                </div>
              );
            })}
          </div>
          <div className='flex w-full gap-2 items-center justify-end'>
            {uploadProgress === Status.ONGOING ? (
              <LoadingSpinner containerClassName='!w-auto' />
            ) : uploadProgress === Status.SUCCESS && statusMessage ? (
              <p className='text-bedrock-p text-bedrock-black'>{statusMessage}</p>
            ) : null}
            <button
              onClick={handleOnClickUpload}
              disabled={isUploadOngoing}
              className='btn-bedrock-secondary disabled:opacity-50 disabled:pointer-events-none w-1/4'
            >
              Select files
            </button>
            <button
              onClick={() => {
                onImportClick(uniqueBulkEmailListRef.current);
                bulkEmailListRef.current = [];
                uniqueBulkEmailListRef.current = [];
                setFileStatusMapping({});
                setStatusMessage('');
                setUploadProgress(Status.IDLE);
                handleCloseModal();
              }}
              className='btn-bedrock-primary capitalize w-1/4'
              disabled={isImportDisabled}
            >
              Process
            </button>
          </div>
        </div>
      </animated.div>
    </div>
  );
};

export const BulkEmailUploadModal = forwardRef(BulkEmailUploadModalComponent);
